import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { create } from '@thoughtspot/logger';
import {
    isMfeCdnUrl,
    Microfrontend,
    ReleaseJson,
} from '@thoughtspot/microfrontends';
import { loadCssDynamically } from '/@utils/browser.util';
import { MICRO_FRONTEND_DYNAMIC_IMPORT } from './microfrontends-config';

declare const window: any;

export type MfeResources = {
    mfeJsResourceUrl: string;
    mfeCssResourceUrl: string;
    mfeReleaseVersion: string;
    mfeBuildNumber: string;
};

const logger = create('microfrontends-util');
const initializedMicroFrontends: any = {};

export const getMfeResourceUrls = async (
    mfeName: Microfrontend,
): Promise<MfeResources> => {
    const { queryParams } = getQueryParamsAndQueryString();
    let mfeJsResourceUrl: string;
    let mfeCssResourceUrl: string;
    let mfeReleaseVersion: string;
    let mfeBuildNumber: string;
    const releaseJsonUrl = queryParams[`${mfeName}Mfe`];
    if (releaseJsonUrl) {
        // fetch and parse release json
        try {
            if (!isMfeCdnUrl(releaseJsonUrl)) {
                throw new Error(`${releaseJsonUrl} is not an MFE CDN host`);
            }
            const response = await fetch(releaseJsonUrl);
            const releaseJson: ReleaseJson = await response.json();
            const jsUrl =
                releaseJson?.resources?.js &&
                [
                    releaseJson.basePath,
                    releaseJson.app,
                    releaseJson.resources.js,
                ].join('/');
            const cssUrl =
                releaseJson?.resources?.css &&
                [
                    releaseJson.basePath,
                    releaseJson.app,
                    releaseJson.resources.css,
                ].join('/');
            // `basePath` comes out of the fetched JSON, so an allow-listed
            // release json can still point its assets off-CDN.
            if (!isMfeCdnUrl(jsUrl)) {
                throw new Error(`${jsUrl} is not an MFE CDN host`);
            }
            if (cssUrl && !isMfeCdnUrl(cssUrl)) {
                throw new Error(`${cssUrl} is not an MFE CDN host`);
            }
            mfeJsResourceUrl = jsUrl;
            mfeCssResourceUrl = cssUrl;
            mfeReleaseVersion = releaseJson.baseVersion;
            mfeBuildNumber = releaseJson.buildTag.split('-')[1];
        } catch (error) {
            logger.error(
                `Failed to fetch release json for ${mfeName}: ${
                    error instanceof Error ? error.message : String(error)
                }`,
            );
        }
    }
    if (
        !mfeJsResourceUrl &&
        queryParams[`${mfeName}_js`] &&
        queryParams[`${mfeName}_css`]
    ) {
        // To be used by MFE CI
        // Ensure MFE Folder names do not have an underscore
        const jsUrl = queryParams[`${mfeName}_js`];
        const cssUrl = queryParams[`${mfeName}_css`];
        if (isMfeCdnUrl(jsUrl) && isMfeCdnUrl(cssUrl)) {
            mfeJsResourceUrl = jsUrl;
            mfeCssResourceUrl = cssUrl;
        } else {
            logger.error(
                `Ignoring ?${mfeName}_js/${mfeName}_css override: ${jsUrl} and ${cssUrl} are not both on an MFE CDN host`,
            );
        }
    }
    if (!mfeJsResourceUrl && window?.blink?.microfrontends?.[mfeName]?.js) {
        mfeJsResourceUrl = window?.blink?.microfrontends?.[mfeName]?.js;
        mfeCssResourceUrl = window?.blink?.microfrontends?.[mfeName]?.css;
        mfeReleaseVersion =
            window?.blink?.microfrontends?.[mfeName].baseVersion;
        mfeBuildNumber = window?.blink?.microfrontends?.[
            mfeName
        ].buildTag.split('-')[1];
    }
    return {
        mfeJsResourceUrl,
        mfeCssResourceUrl,
        mfeReleaseVersion,
        mfeBuildNumber,
    };
};

export const initMicroFrontend = async (mfeName: Microfrontend) => {
    if (!initializedMicroFrontends[mfeName]) {
        const {
            mfeJsResourceUrl,
            mfeCssResourceUrl,
        } = await getMfeResourceUrls(mfeName);
        if (mfeCssResourceUrl || mfeJsResourceUrl)
            logger.info(
                `Fetching JS and CSS resources from ${mfeJsResourceUrl} and ${mfeCssResourceUrl}`,
            );
        loadCssDynamically(mfeJsResourceUrl && mfeCssResourceUrl);
        initializedMicroFrontends[mfeName] = mfeJsResourceUrl
            ? // eslint-disable-next-line
              await import(/* @vite-ignore */ mfeJsResourceUrl)
            : await MICRO_FRONTEND_DYNAMIC_IMPORT[mfeName]();
        logger.info(`MFE ${mfeName} initialized`);
    }
};

export const getMicroFrontendHook = async (
    mfeName: Microfrontend,
    hookName: any,
) => {
    await initMicroFrontend(mfeName);
    const mfe = initializedMicroFrontends[mfeName];
    return mfe[hookName];
};

export const getMicroFrontendComponent = async (
    mfeName: Microfrontend,
    componentName: any,
) => {
    await initMicroFrontend(mfeName);
    const mfe = initializedMicroFrontends[mfeName];
    return {
        default: mfe[componentName],
    };
};
